import React, { useState } from 'react';
import { supabase } from '../../../../components/supabase/config';
import 'bootstrap/dist/css/bootstrap.min.css';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Normalizes many possible inputs into:
 *   "MM-DD-YYYY HH:MM"
 *
 * Rules:
 * 1) If only date exists (MM-DD-YYYY or MM/DD/YYYY or YYYY-MM-DD), time defaults to "00:00".
 * 2) If time exists, keep it.
 */
function normalizeToMMDDYYYY_HHMM(input) {
  if (!input) return '';

  const s = String(input).trim();
  if (!s) return '';

  // ISO datetime: "YYYY-MM-DDTHH:MM..."
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (isoMatch) {
    const [, yyyy, mm, dd, HH, MM] = isoMatch;
    return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
  }

  // ISO date only: "YYYY-MM-DD"
  const isoDateOnlyMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnlyMatch) {
    const [, yyyy, mm, dd] = isoDateOnlyMatch;
    return `${mm}-${dd}-${yyyy} 00:00`;
  }

  // "MM-DD-YYYY" optionally with time " HH:MM"
  const mdYDash = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (mdYDash) {
    const [, mm, dd, yyyy, HH, MM] = mdYDash;
    return `${mm}-${dd}-${yyyy} ${HH && MM ? `${HH}:${MM}` : '00:00'}`;
  }

  // "MM/DD/YYYY" optionally with time " HH:MM"
  const mdYSlash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (mdYSlash) {
    const [, mm, dd, yyyy, HH, MM] = mdYSlash;
    return `${mm}-${dd}-${yyyy} ${HH && MM ? `${HH}:${MM}` : '00:00'}`;
  }

  // Fallback: try Date parsing
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const yyyy = d.getFullYear();
    const HH = pad2(d.getHours());
    const MM = pad2(d.getMinutes());
    return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
  }

  return s;
}

/**
 * Converts normalized display "MM-DD-YYYY HH:MM"
 * -> <input type="datetime-local"> value "YYYY-MM-DDTHH:MM"
 */
function toDatetimeLocalValue(displayValue) {
  const normalized = normalizeToMMDDYYYY_HHMM(displayValue);
  if (!normalized) return '';

  const match = normalized.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})$/);
  if (!match) return '';

  const [, mm, dd, yyyy, HH, MM] = match;
  return `${yyyy}-${mm}-${dd}T${HH}:${MM}`;
}

/**
 * Converts <input type="datetime-local"> value "YYYY-MM-DDTHH:MM"
 * -> preferred string format "MM-DD-YYYY HH:MM"
 */
function fromDatetimeLocalValue(rawValue) {
  if (!rawValue) return '';
  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return '';
  const [, yyyy, mm, dd, HH, MM] = match;
  return `${mm}-${dd}-${yyyy} ${HH}:${MM}`;
}

const App = () => {
  const statusOptions = [
    'Bangkok Warehouse',
    'Loaded',
    'Loading',
    'Sailing',
    'Clearance',
    'delivered',
    'arrived',
    'Manila Warehouse',
    'berthing',
    'out for delivery',
    'not found',
    'no tracking',
    'sailing near Phil.SEA',
    'Manila port',
    'Sorting of Boxes',
  ];

  const [selectedTracking, setSelectedTracking] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [editingStatusId, setEditingStatusId] = useState(null);

  // NOTE: keep state name updatedStatus (now used by dropdown)
  const [updatedStatus, setUpdatedStatus] = useState('');
  const [updatedDateRaw, setUpdatedDateRaw] = useState(''); // datetime-local raw

  const [newStatus, setNewStatus] = useState('');
  const [newDateRaw, setNewDateRaw] = useState('');

  const [isSearching, setIsSearching] = useState(false);

  const fetchTrackingById = async (trackingId) => {
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('trackingfbt_audit')
        .select('tracking_id, date_loaded, remarks, audit_id')
        .eq('tracking_id', trackingId)
        .order('audit_id', { ascending: true });

      if (error) {
        console.error('Error fetching tracking:', error);
        setSelectedTracking(null);
        return;
      }

      if (!data || data.length === 0) {
        setSelectedTracking(null);
        return;
      }

      const trackingNumber = data[0].tracking_id;

      const statuses = data.map((item) => ({
        id: item.audit_id,
        status: item.remarks || 'No status available',
        timestamp: normalizeToMMDDYYYY_HHMM(item.date_loaded || ''),
      }));

      setSelectedTracking({ trackingNumber, statuses });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    const trackingId = searchQuery.trim();
    if (!trackingId) return;
    await fetchTrackingById(trackingId);
  };

  const handleEditStatus = (status) => {
    setEditingStatusId(status.id);

    // When editing starts, set dropdown selected to current status (as-is)
    setUpdatedStatus(status.status);

    setUpdatedDateRaw(toDatetimeLocalValue(status.timestamp));
  };

  const cancelEdit = () => {
    setEditingStatusId(null);
    setUpdatedStatus('');
    setUpdatedDateRaw('');
  };

  const saveEditedStatus = async () => {
    if (!editingStatusId || !selectedTracking) return;

    // Validation: must choose from dropdown (no blanks)
    if (!updatedStatus) {
      alert('Please choose the status');
      return;
    }

    const formattedForDb = fromDatetimeLocalValue(updatedDateRaw);
    if (!formattedForDb) {
      alert('Please select a valid date and time.');
      return;
    }

    const { error } = await supabase
      .from('trackingfbt_audit')
      .update({
        remarks: updatedStatus,
        date_loaded: formattedForDb,
      })
      .eq('audit_id', editingStatusId);

    if (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status.');
      return;
    }

    alert('Status updated successfully.');
    cancelEdit();
    await fetchTrackingById(selectedTracking.trackingNumber);
  };

  const handleDeleteStatus = async (statusId) => {
    if (!selectedTracking) return;

    const { error } = await supabase.from('trackingfbt_audit').delete().eq('audit_id', statusId);

    if (error) {
      console.error('Error deleting status:', error);
      alert('Failed to delete status.');
      return;
    }

    alert('Status deleted successfully.');
    if (editingStatusId === statusId) cancelEdit();
    await fetchTrackingById(selectedTracking.trackingNumber);
  };

  const handleAddStatus = async () => {
    if (!selectedTracking) return;

    if (!newStatus) {
      alert('Please choose the status');
      return;
    }

    if (!newDateRaw) {
      alert('Please select a valid date and time.');
      return;
    }

    const formattedForDb = fromDatetimeLocalValue(newDateRaw);
    if (!formattedForDb) {
      alert('Please select a valid date and time.');
      return;
    }

    try {
      const { data: maxAuditIdData, error: maxAuditIdError } = await supabase
        .from('trackingfbt_audit')
        .select('audit_id')
        .order('audit_id', { ascending: false })
        .limit(1);

      if (maxAuditIdError) {
        console.error('Error fetching max audit_id:', maxAuditIdError);
        alert('Failed to add status.');
        return;
      }

      const nextAuditId =
        maxAuditIdData && maxAuditIdData.length > 0 ? maxAuditIdData[0].audit_id + 1 : 1;

      const { error } = await supabase.from('trackingfbt_audit').insert([
        {
          audit_id: nextAuditId,
          tracking_id: selectedTracking.trackingNumber,
          remarks: newStatus,
          date_loaded: formattedForDb,
        },
      ]);

      if (error) {
        console.error('Error adding status:', error);
        alert('Failed to add status.');
        return;
      }

      setNewStatus('');
      setNewDateRaw('');
      alert('New status added successfully.');
      await fetchTrackingById(selectedTracking.trackingNumber);
    } catch (error) {
      console.error('Unexpected error:', error);
      alert('An error occurred.');
    }
  };

  return (
    <div className="container mt-5">
      <h1 className="text-center mb-4">Search Tracking Number</h1>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Enter tracking number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="btn btn-primary mt-2" onClick={handleSearch} disabled={isSearching}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Status History */}
      {selectedTracking ? (
        <div>
          <h3 className="mt-4">Tracking Number: {selectedTracking.trackingNumber}</h3>
          <h4>Status History</h4>

          {/* Add Status UI */}
          <div className="mb-3 p-3 border rounded">
            <div className="mb-2">
              <select
                className="form-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">-- Select status --</option>
                {statusOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <input
                className="form-control"
                type="datetime-local"
                value={newDateRaw}
                onChange={(e) => setNewDateRaw(e.target.value)}
              />
              <div className="form-text">
                Will save as:{' '}
                <strong>{newDateRaw ? fromDatetimeLocalValue(newDateRaw) : 'MM-DD-YYYY HH:MM'}</strong>
              </div>
            </div>

            <button className="btn btn-success" onClick={handleAddStatus}>
              Add Status
            </button>
          </div>

          <ul className="list-group">
            {selectedTracking.statuses.map((status) => (
              <li key={status.id} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start">
                  <div style={{ flex: 1, paddingRight: 12 }}>
                    <strong>Status:</strong>{' '}
                    {editingStatusId === status.id ? (
                      // CHANGED: show dropdown ONLY when editing
                      <select
                        className="form-select mb-2"
                        value={updatedStatus}
                        onChange={(e) => setUpdatedStatus(e.target.value)}
                      >
                        {/* keep current value "as-is" by injecting it if not in list */}
                        {updatedStatus && !statusOptions.includes(updatedStatus) && (
                          <option value={updatedStatus}>{updatedStatus}</option>
                        )}
                        <option value="">-- Select status --</option>
                        {statusOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      // NOT editing: show original DB value as-is
                      status.status
                    )}

                    <strong> Date:</strong>{' '}
                    {editingStatusId === status.id ? (
                      <>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={updatedDateRaw}
                          onChange={(e) => setUpdatedDateRaw(e.target.value)}
                        />
                        <div className="form-text">
                          Will save as:{' '}
                          <strong>
                            {updatedDateRaw ? fromDatetimeLocalValue(updatedDateRaw) : 'MM-DD-YYYY HH:MM'}
                          </strong>
                        </div>
                      </>
                    ) : status.timestamp ? (
                      status.timestamp
                    ) : (
                      'No date available'
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    {editingStatusId === status.id ? (
                      <>
                        <button className="btn btn-success btn-sm" onClick={saveEditedStatus}>
                          Save
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-warning btn-sm" onClick={() => handleEditStatus(status)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteStatus(status.id)}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-center">Please search for a valid tracking number.</p>
      )}
    </div>
  );
};

export default App;
